import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Loader2, Lock, Save, Shield } from "lucide-react";
import * as React from "react";

import type { AdminSetMfaPolicyRequest, AdminSetPasswordPolicyRequest } from "@ando/core";
import { Button, Checkbox, useToast } from "@ando/ui";

import {
  useAdminOrgMfaPolicy,
  useAdminOrgPasswordPolicy,
  useSetAdminOrgMfaPolicy,
  useSetAdminOrgPasswordPolicy,
} from "@/features/admin/hooks/useAdminOrg";

import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import { LoadingRow } from "./_shared";

/**
 * Security tab — MFA toggle + NIST-aligned password policy +
 * password-strength tester.
 *
 * v1 reference: ``OrgSecurityTab.tsx`` (190 LoC) +
 * ``PasswordStrengthMeter.tsx`` (58 LoC). The v2 surface now mounts
 * ``<PasswordStrengthMeter>`` next to the policy editor so admins can
 * validate the active policy against a candidate password without
 * leaving the tab — matches the v1 UX 1:1 with Ando tokens.
 */

export interface OrgSecurityTabProps {
  orgId: string;
}

const COMPLEXITY: ReadonlyArray<
  [keyof Omit<AdminSetPasswordPolicyRequest, "min_length">, MessageDescriptor]
> = [
  ["has_uppercase", msg`Maiuscole (A-Z)`],
  ["has_lowercase", msg`Minuscole (a-z)`],
  ["has_number", msg`Numeri (0-9)`],
  ["has_symbol", msg`Simboli (!@#$)`],
];

export function OrgSecurityTab({ orgId }: OrgSecurityTabProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const mfaQuery = useAdminOrgMfaPolicy(orgId);
  const pwdQuery = useAdminOrgPasswordPolicy(orgId);
  const saveMfa = useSetAdminOrgMfaPolicy(orgId);
  const savePwd = useSetAdminOrgPasswordPolicy(orgId);

  const [pwd, setPwd] = React.useState<AdminSetPasswordPolicyRequest>({
    min_length: 12,
    has_uppercase: false,
    has_lowercase: false,
    has_number: false,
    has_symbol: false,
  });
  // Admin-only "test password" — never sent to BE, purely local for
  // the live strength meter / requirements checklist. Cleared on tab
  // unmount via component lifecycle.
  const [testPassword, setTestPassword] = React.useState("");

  React.useEffect(() => {
    if (pwdQuery.data) setPwd(pwdQuery.data);
  }, [pwdQuery.data]);

  const lenErr =
    pwd.min_length < 8 || pwd.min_length > 128
      ? i18n._(msg`Lunghezza tra 8 e 128 caratteri.`)
      : null;

  const handleMfaToggle = (forceMfa: boolean): void => {
    saveMfa.mutate({ force_mfa: forceMfa } satisfies AdminSetMfaPolicyRequest, {
      onSuccess: () =>
        toast.success(i18n._(msg`Policy MFA aggiornata`), {
          description: forceMfa
            ? i18n._(msg`MFA obbligatorio per i membri.`)
            : i18n._(msg`MFA non più obbligatorio.`),
        }),
      onError: (err) =>
        toast.error(i18n._(msg`MFA`), {
          description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  const handleSavePwd = (): void => {
    savePwd.mutate(pwd, {
      onSuccess: () =>
        toast.success(i18n._(msg`Password policy salvata`), {
          description: i18n._(msg`Lunghezza minima ${pwd.min_length} caratteri.`),
        }),
      onError: (err) =>
        toast.error(i18n._(msg`Password policy`), {
          description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  if (mfaQuery.isLoading || pwdQuery.isLoading) {
    return <LoadingRow label={i18n._(msg`Caricamento policy…`)} />;
  }

  const forceMfa = mfaQuery.data?.force_mfa ?? false;

  return (
    <div className="flex flex-col gap-4">
      <section
        aria-labelledby="org-security-mfa"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3 flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-ando-orange-soft/40 text-accent">
            <Shield size={16} aria-hidden />
          </div>
          <div>
            <h2 id="org-security-mfa" className="font-semibold text-[13px] text-ink-1">
              {i18n._(msg`Multi-Factor Authentication`)}
            </h2>
            <p className="text-[12px] text-ink-3">
              {i18n._(msg`Impone TOTP o passkey a tutti i membri.`)}
            </p>
          </div>
        </div>
        <div className="flex max-w-md items-center justify-between gap-3">
          <div className="text-[12.5px] text-ink-2">
            <p className="font-medium text-ink-1">{i18n._(msg`Forza MFA`)}</p>
            <p className="text-[11.5px] text-ink-3">
              {forceMfa
                ? i18n._(msg`MFA è obbligatorio per i nuovi login.`)
                : i18n._(msg`MFA è opzionale per i membri.`)}
            </p>
          </div>
          <Checkbox
            role="switch"
            aria-checked={forceMfa}
            aria-label={i18n._(msg`Forza MFA per i membri`)}
            checked={forceMfa}
            disabled={saveMfa.isPending}
            onChange={(e) => handleMfaToggle(e.target.checked)}
          />
        </div>
      </section>

      <section
        aria-labelledby="org-security-pwd"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3 flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-warning/15 text-warning-foreground">
            <Lock size={16} aria-hidden />
          </div>
          <div>
            <h2 id="org-security-pwd" className="font-semibold text-[13px] text-ink-1">
              {i18n._(msg`Password policy`)}
            </h2>
            <p className="text-[12px] text-ink-3">
              {i18n._(msg`NIST SP 800-63B — lunghezza prima di complessità.`)}
            </p>
          </div>
        </div>
        <div className="max-w-md space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="pwd-min-length" className="block text-[12px] text-ink-2">
              {i18n._(msg`Lunghezza minima:`)}{" "}
              <span className="font-semibold text-ink-1 tabular-nums">{pwd.min_length}</span>{" "}
              {i18n._(msg`caratteri`)}
            </label>
            <input
              id="pwd-min-length"
              type="range"
              min={8}
              max={128}
              value={pwd.min_length}
              onChange={(e) => setPwd((p) => ({ ...p, min_length: Number(e.target.value) }))}
              aria-valuemin={8}
              aria-valuemax={128}
              aria-valuenow={pwd.min_length}
              className="w-full accent-accent"
            />
            <div className="flex justify-between text-[10.5px] text-ink-3 tabular-nums">
              <span>8</span>
              <span>128</span>
            </div>
            {lenErr ? <span className="block text-[11px] text-destructive">{lenErr}</span> : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {COMPLEXITY.map(([key, label]) => (
              <label
                key={key}
                className="flex items-center justify-between gap-2 text-[12.5px] text-ink-2"
              >
                <span>{i18n._(label)}</span>
                <Checkbox
                  role="switch"
                  aria-checked={pwd[key]}
                  checked={pwd[key]}
                  onChange={(e) => setPwd((p) => ({ ...p, [key]: e.target.checked }))}
                />
              </label>
            ))}
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleSavePwd}
            disabled={savePwd.isPending || !!lenErr}
          >
            {savePwd.isPending ? (
              <Loader2 size={13} aria-hidden className="mr-1.5 animate-spin" />
            ) : (
              <Save size={13} aria-hidden className="mr-1.5" />
            )}
            {i18n._(msg`Salva password policy`)}
          </Button>
        </div>
      </section>

      <section
        aria-labelledby="org-security-pwd-test"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3 flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-ando-orange-soft/40 text-accent">
            <Lock size={16} aria-hidden />
          </div>
          <div>
            <h2 id="org-security-pwd-test" className="font-semibold text-[13px] text-ink-1">
              {i18n._(msg`Tester forza password`)}
            </h2>
            <p className="text-[12px] text-ink-3">
              {i18n._(
                msg`Digita una password di esempio per validare la policy attiva prima di comunicarla agli utenti. Nulla viene inviato al server.`,
              )}
            </p>
          </div>
        </div>
        <div className="max-w-md space-y-3">
          <div className="space-y-1.5">
            <label
              htmlFor="pwd-test-input"
              className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
            >
              {i18n._(msg`Password di prova`)}
            </label>
            <input
              id="pwd-test-input"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={testPassword}
              onChange={(e) => setTestPassword(e.target.value)}
              placeholder={i18n._(msg`Es. Acme!2026-EarlyAccess`)}
              className="h-8 w-full rounded-md border border-hairline bg-card px-2.5 font-mono text-[12.5px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            />
          </div>
          <PasswordStrengthMeter password={testPassword} policy={pwd} />
        </div>
      </section>
    </div>
  );
}
